import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';
import { c, card, chart } from '../../theme';
import { podTone } from './badges';

// The VP page's trend block: four charts, one line or bar series per pod.
//
// This is the old page's own section, rebuilt from its bundle rather than from
// a screenshot — four cards in a two-column grid, each 280px tall, in its
// order: service level, abandon rate, AHT, handled volume. Three are
// multi-line and the fourth is stacked bars, because handled is a quantity
// that adds up across pods and the other three are rates that do not.
//
// Colours are the pods' own identities (badges.POD_TONE, copied value for
// value from the old app), so a line here is the same colour as that pod's
// chip in the ranking table below it. Nine series is past what either shared
// chart palette can separate, which is exactly why identity colour rather
// than a sequence is right: the reader is matching a line to a named pod they
// can already see, not telling two anonymous series apart.
//
// ── Grain ───────────────────────────────────────────────────────────────────
// A day per point, except on Today and Yesterday where a single point is not
// a trend and the charts switch to hours. The server sends `podHourly` only
// for a one-day window; the page still gates on the range, so a custom range
// of one day reads as a day, which is what the old page does.
//
// The two grains count HANDLED differently and the subtitle says so. A day is
// distinct conversations (cc_distinct_daily); an hour can only be legs,
// because the distinct count is stored per day and has no hour. The old app
// draws the same distinction in the same place.
//
// ── The AHT chart is NOT a copy ─────────────────────────────────────────────
// The old page's "AHT by day" reads an `aht` field that its own API returns
// as 0 for every pod-day: all 279 of them across August 2026, and the same
// today. Its chart is a flat line along the axis and has been for as long as
// there is data to check. Copying that would be copying a fault, so this one
// draws the real figure — the same Voice+VIP handle time the gauge above it
// shows — and the card says where the two pages differ. Its hourly rows do
// carry a real AHT, so only the daily grain is affected there.

const AXIS = { fontSize: 11, fill: chart.axisText };
const GRID = chart.grid;
const TOOLTIP = {
  contentStyle: { fontSize: 12, borderColor: chart.tooltipBorder, borderRadius: 6 },
  labelStyle: { color: c.textStrong, fontWeight: 600 },
};

// '2026-08-04' -> '8/4'. An hour label ('07:00') is already what it should
// read as, so it passes through — the old app's two tick formatters.
const tickFor = (hourly) => (hourly ? (v) => v : (v) => {
  const p = String(v || '').split('-');
  return p.length === 3 ? `${Number(p[1])}/${Number(p[2])}` : v;
});

/**
 * { pod: [{date, ...}] } -> [{date, [pod]: value}], ordered by date.
 *
 * Pods do not all have rows for the same days — a pod that took no calls on a
 * Sunday has no row — so the union of dates is built from every pod rather
 * than from the first one, and a missing pod-day stays undefined so the line
 * bridges it (`connectNulls`) instead of dropping to zero.
 */
export function pivot(series, field) {
  const byDate = new Map();
  for (const [pod, rows] of Object.entries(series || {})) {
    for (const r of rows || []) {
      const row = byDate.get(r.date) || { date: r.date };
      row[pod] = r[field];
      byDate.set(r.date, row);
    }
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}

function Card({ title, subtitle, note, children }) {
  return (
    <section style={s.card} aria-label={title}>
      <div style={s.head}>
        <h3 style={s.title}>{title}</h3>
        {subtitle && <span style={s.sub}>{subtitle}</span>}
      </div>
      <div style={s.body}>
        <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
      </div>
      {note && <p style={s.note}>{note}</p>}
    </section>
  );
}

function PodLines({ pods, rows, domain, unit, fmt, tickFmt, children }) {
  return (
    <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
      <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="date" tickFormatter={tickFmt} tick={AXIS} tickLine={false}
        axisLine={{ stroke: chart.tooltipBorder }} minTickGap={28} />
      <YAxis domain={domain} tick={AXIS} tickLine={false} axisLine={{ stroke: chart.tooltipBorder }}
        width={46} unit={unit} tickFormatter={fmt} />
      <Tooltip {...TOOLTIP} labelFormatter={tickFmt} formatter={(v) => (v == null ? '—' : fmt(v))} />
      {children}
      {pods.map((pod) => (
        <Line key={pod} type="monotone" dataKey={pod} name={pod} stroke={podTone(pod).line}
          strokeWidth={2} dot={false} activeDot={{ r: 3 }} connectNulls isAnimationActive={false} />
      ))}
    </LineChart>
  );
}

export default function VpPodTrends({ podDaily, podHourly, slTarget = 95, hourly = false }) {
  const series = hourly && podHourly ? podHourly : podDaily;
  const pods = Object.keys(series || {});
  if (!pods.length) return <div style={s.empty}>No pods in scope.</div>;

  const byHour = hourly && !!podHourly;
  const grain = byHour ? 'hour' : 'day';
  const tick = tickFor(byHour);
  const sl = pivot(series, 'serviceLevel');
  const ab = pivot(series, 'abandonPct');
  const aht = pivot(series, 'aht');
  const vol = pivot(series, 'handled');

  return (
    <>
      <style>{css}</style>
      <div className="cc-pod-trends">
        <Card title={`Service level by ${grain}`} subtitle={`Voice · target ${slTarget}%`}>
          <PodLines pods={pods} rows={sl} domain={[0, 100]} unit="%" fmt={(v) => `${v}%`} tickFmt={tick}>
            <ReferenceLine y={slTarget} stroke={c.brand} strokeDasharray="4 4"
              label={{ value: `${slTarget}%`, position: 'insideTopRight', fontSize: 10, fill: c.brand }} />
          </PodLines>
        </Card>

        <Card title={`Abandon rate by ${grain}`} subtitle="% of offered">
          <PodLines pods={pods} rows={ab} domain={[0, 'auto']} fmt={(v) => `${v}%`} tickFmt={tick} />
        </Card>

        <Card title={`AHT by ${grain}`} subtitle="seconds"
          note={byHour ? null : 'The old dashboard returns 0 for this figure on every pod-day, so its own chart is flat. This is the handle time the gauges above use.'}>
          <PodLines pods={pods} rows={aht} domain={[0, 'auto']} fmt={(v) => `${Math.round(v)}s`} tickFmt={tick} />
        </Card>

        <Card title={`Handled volume by ${grain}`}
          subtitle={byHour ? 'legs · stacked by pod' : 'distinct conversations · stacked by pod'}>
          <BarChart data={vol} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
            <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tickFormatter={tick} tick={AXIS} tickLine={false}
              axisLine={{ stroke: chart.tooltipBorder }} minTickGap={28} />
            <YAxis tick={AXIS} tickLine={false} axisLine={{ stroke: chart.tooltipBorder }} width={46} />
            <Tooltip {...TOOLTIP} labelFormatter={tick} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
            {pods.map((pod) => (
              <Bar key={pod} dataKey={pod} name={pod} stackId="handled"
                fill={podTone(pod).line} isAnimationActive={false} />
            ))}
          </BarChart>
        </Card>
      </div>
    </>
  );
}

// A real stylesheet rather than an inline style object, because the fix IS a
// media query and an inline object cannot hold one.
//
// Fixed at two columns, not `auto-fit`. There are exactly four cards here, and
// auto-fit packed as many 420px columns as the width allowed — so on a wide
// screen the set reflowed to three-across-then-one, or four in a row, and the
// 2×2 block the CS team reads these in fell apart at exactly the widths the
// floor's monitors use. Four cards want two columns at every width that fits
// two.
//
// `minmax(0, 1fr)` rather than `1fr`: a grid track's implicit minimum is
// auto, and a Recharts container inside an auto-minimum track refuses to
// shrink below its own rendered content, so the row overflows instead of the
// charts getting narrower.
//
// 1150px is where two 420px cards stop fitting, measured rather than picked:
// the sidebar is 240px and .sh-content adds 28px of padding either side, so
// content is viewport − 296, and 2 × 420 + 14 of gap needs 854 of it.
const css = `
.cc-pod-trends {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
@media (max-width: 1150px) {
  .cc-pod-trends { grid-template-columns: minmax(0, 1fr); }
}
`;

const s = {
  card:  { ...card.base, display: 'flex', flexDirection: 'column' },
  head:  { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px',
    borderBottom: `1px solid ${c.border}`, padding: '10px 16px' },
  title: { fontSize: '13px', fontWeight: 650, color: c.text, margin: 0 },
  sub:   { fontSize: '11px', color: c.textFaint },
  body:  { height: '280px', padding: '12px 8px' },
  note:  { fontSize: '11px', color: c.textMuted, margin: 0, padding: '0 16px 10px' },
  empty: { ...card.base, border: `1px dashed ${c.borderStrong}`, padding: '40px 24px',
    textAlign: 'center', fontSize: '13px', color: c.textMuted },
};
